import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { BlurView } from 'expo-blur';
import { Button, SIZES } from '@/components/button';
import { ChatStatus } from '@/enums';
import { ConfirmationActionContent } from '@/components/confirmation-action-content';
import { createStyles } from '@/contexts/theme-context';
import {
  DuplicateOrderToAccountsMutation,
  DuplicateOrderToAccountsMutationVariables,
} from '@/types';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { FilterPills } from '@/components/filter-pills';
import { gql, useMutation } from '@apollo/client';
import { LOCATION_MANAGER_ROUTES } from '@/navigators/routes';
import { LocationManagerStackParams } from '@/navigators/types';
import {
  LocationOption,
  LocationPickerList,
} from '@/components/locations/location-picker-list';
import { ModalContainer } from '@/components/modal-container';
import { MultiSelectFilterType } from '@/hooks/use-location-picker-filter-pills';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { PillButton, PillButtonType } from '@/components/pill-button';
import { RecyclerListRef } from '@/components/recycler-list';
import { SearchFiltersActionSheet } from '@/components/search-filters/search-filters-action-sheet';
import { SearchInput } from '@/components/search-input';
import { StackScreenProps } from '@react-navigation/stack';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLocationPickerData } from '@/hooks/use-location-picker-data';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import { View } from 'react-native';

export const DUPLICATE_ORDER_TO_ACCOUNTS_MUTATION = gql`
  mutation DuplicateOrderToAccounts(
    $restaurantUUID: uuid!
    $sourceOrderUUID: uuid!
    $supplierUUID: uuid!
    $targetRestaurantsUUIDs: [uuid!]!
  ) {
    copyRestaurantOrder(
      restaurant_id: $restaurantUUID
      source_order_id: $sourceOrderUUID
      supplier_id: $supplierUUID
      target_restaurant_ids: $targetRestaurantsUUIDs
    ) {
      success
    }
  }
`;

export type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.DuplicateOrder
>;

export const LocationPickerDuplicateScreen: React.FC<Props> = ({
  route,
  navigation,
}) => {
  const { restaurantName, orderUUID } = route.params;
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] =
    useState(false);
  const [selectedAccountUUIDs, setSelectedAccountUUIDs] = useState<Set<string>>(
    new Set(),
  );
  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { showErrorToast, showToast } = useToasts();
  const listRef = useRef<RecyclerListRef<LocationOption>>(null);

  const {
    restaurantRelationships: baseRestaurantRelationships,
    selectedRestaurantUUID,
    selectedRestaurantRelationship,
  } = useAuthenticatedUserContext();

  const filteredRestaurantRelationshipsWithoutProspects = useMemo(
    () =>
      baseRestaurantRelationships.filter(
        (relationship) =>
          relationship.uuid !== selectedRestaurantUUID &&
          relationship.chatStatus !== ChatStatus.PROSPECT,
      ),
    [baseRestaurantRelationships, selectedRestaurantUUID],
  );

  const {
    accountModeFilter,
    dataStatus,
    filteredRestaurantRelationships,
    getPillRef,
    hasMultipleSuppliers,
    multiSelectFilters,
    onDisplayTextChange,
    pillButtonProps,
    searching,
    searchText,
  } = useLocationPickerData(filteredRestaurantRelationshipsWithoutProspects);

  const [duplicateOrderToAccounts] = useMutation<
    DuplicateOrderToAccountsMutation,
    DuplicateOrderToAccountsMutationVariables
  >(DUPLICATE_ORDER_TO_ACCOUNTS_MUTATION);

  const renderPillButton = useCallback((button: PillButtonType) => {
    return <PillButton button={button} key={button.label} ref={button.ref} />;
  }, []);

  const handleScrollToTop = () => {
    if (listRef.current) {
      listRef.current.scrollTo({ animated: true, y: 0 });
    }
  };

  const onSelectAccount = (relationship: EmployeeRestaurant) => {
    setSelectedAccountUUIDs((currentSelectedAccountUUIDs) => {
      if (currentSelectedAccountUUIDs.has(relationship.uuid)) {
        return new Set(
          [...currentSelectedAccountUUIDs].filter(
            (uuid) => uuid !== relationship.uuid,
          ),
        );
      }
      return new Set([...currentSelectedAccountUUIDs, relationship.uuid]);
    });
  };

  const onConfirmDuplicateOrder = async () => {
    setShowDuplicateConfirmation(false);
    try {
      await duplicateOrderToAccounts({
        variables: {
          restaurantUUID: selectedRestaurantUUID,
          sourceOrderUUID: orderUUID,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
          targetRestaurantsUUIDs: [...selectedAccountUUIDs],
        },
      });
      showToast({
        content: onlyOneAccountIsSelected
          ? i18n.t('duplicate_order_processing.singular')
          : i18n.t('duplicate_order_processing.plural', {
              count: selectedAccountUUIDs.size,
            }),
        type: Type.PRIMARY,
      });
      navigation.goBack();
    } catch {
      showErrorToast();
    }
  };

  const navigationHeaderProps = useMemo(() => {
    const filteredRestaurantUUIDs = new Set(
      filteredRestaurantRelationships.map((rr) => rr.uuid),
    );
    const intersection = new Set(
      [...filteredRestaurantUUIDs].filter((x) => selectedAccountUUIDs.has(x)),
    );
    const isPartiallySelected =
      intersection.size > 0 &&
      intersection.size !== filteredRestaurantUUIDs.size;

    return {
      onHeaderPress: handleScrollToTop,
      showShadow: true,
      subheader: (
        <>
          <View style={styles.searchContainer}>
            <SearchInput
              autoCorrect={false}
              autoFocus
              containerStyle={styles.searchInput}
              onChangeText={onDisplayTextChange}
              placeholder={i18n.t('search')}
              testID="location-picker-input"
              value={searchText}
            />
          </View>
          {filteredRestaurantRelationships.length > 0 ? (
            <>
              <View collapsable={false}>
                {multiSelectFilters.map((filter) => (
                  <SearchFiltersActionSheet<MultiSelectFilterType>
                    buttonRef={getPillRef(filter.key)}
                    filter={filter}
                    isVisible={filter.isSelecting}
                    key={filter.key}
                    onClose={filter.onClose}
                    onSelect={(_key, selectedValues) => {
                      filter.setSelected(selectedValues);
                      filter.onClose();
                    }}
                    trackingPrefix="duplicate_order_to_accounts_selector"
                  />
                ))}
                <FilterPills
                  buttons={pillButtonProps}
                  renderPillButton={renderPillButton}
                />
              </View>
              <View style={styles.selectAllContainer} testID="select-all-row">
                <Typography variant="heading-sm">
                  {i18n.t('select_all')}
                </Typography>
                <MultiStateCheckbox
                  isPartiallySelected={isPartiallySelected}
                  isSelected={intersection.size > 0}
                  onSelect={() =>
                    setSelectedAccountUUIDs((currentSelectedAccountUUIDs) => {
                      const allSelected = filteredRestaurantRelationships.every(
                        (r) => currentSelectedAccountUUIDs.has(r.uuid),
                      );
                      if (allSelected) {
                        return new Set();
                      }

                      return new Set(
                        filteredRestaurantRelationships.map(
                          (restaurant) => restaurant.uuid,
                        ),
                      );
                    })
                  }
                  trackingData={null}
                />
              </View>
            </>
          ) : null}
        </>
      ),
      title: i18n.t('duplicate_order_from', { restaurantName }),
    };
  }, [
    getPillRef,
    multiSelectFilters,
    onDisplayTextChange,
    pillButtonProps,
    renderPillButton,
    restaurantName,
    filteredRestaurantRelationships,
    searchText,
    selectedAccountUUIDs,
    styles,
  ]);

  const onlyOneAccountIsSelected = selectedAccountUUIDs.size === 1;
  const showAbsoluteBlurFooter = selectedAccountUUIDs.size > 0;

  /*
   * The height of the absolute positioned footer.
   * theme.sk.spacing.spaceLarge + bottom are vertical paddings
   * SIZES.medium is the height of the button in it
   * */
  const absoluteBlurFooterHeight =
    SIZES.medium + theme.sk.spacing.spaceLarge + bottom;

  return (
    <ModalContainer navigationHeaderProps={navigationHeaderProps}>
      <LocationPickerList
        accountModeFilter={accountModeFilter}
        bottomActions={[]}
        chatUnreadCountMap={{}}
        contentContainerStyle={
          // because it covers the last element in the list
          showAbsoluteBlurFooter
            ? {
                paddingBottom: absoluteBlurFooterHeight,
              }
            : null
        }
        dataStatus={dataStatus}
        hasMultipleSuppliers={hasMultipleSuppliers}
        isMultiSelecting
        listRef={listRef}
        onCreateAccount={() => {}}
        onRestaurantSelected={() => {}}
        onViewAccount={() => {}}
        restaurantRelationships={filteredRestaurantRelationships}
        restaurantsWithNewInvoices={new Set()}
        searching={searching}
        selectAccount={onSelectAccount}
        selectedAccountUUIDs={selectedAccountUUIDs}
      />
      {showAbsoluteBlurFooter ? (
        <BlurView
          experimentalBlurMethod="dimezisBlurView"
          style={[styles.blur, { paddingBottom: bottom }]}
        >
          <Button
            onPress={() => setShowDuplicateConfirmation(true)}
            trackingData={null}
          >
            {onlyOneAccountIsSelected
              ? i18n.t('duplicate_order_to.button.singular')
              : i18n.t('duplicate_order_to.button.plural', {
                  count: selectedAccountUUIDs.size,
                })}
          </Button>
        </BlurView>
      ) : null}
      {showDuplicateConfirmation ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowDuplicateConfirmation(false)}
        >
          <ConfirmationActionContent
            contentContainerStyle={styles.confirmationContentContainer}
            message={
              onlyOneAccountIsSelected
                ? i18n.t('duplicate_order_confirmation.singular')
                : i18n.t('duplicate_order_confirmation.plural', {
                    count: selectedAccountUUIDs.size,
                  })
            }
            onPrimaryButtonPress={onConfirmDuplicateOrder}
            onSecondaryButtonPress={() => setShowDuplicateConfirmation(false)}
            primaryButtonLabel={i18n.t(
              onlyOneAccountIsSelected ? 'place_order' : 'place_order',
            )}
            primaryButtonTrackingData={{
              name: 'duplicate_order_to_accounts.confirm_button.press',
              properties: {
                restaurantUUID: selectedRestaurantUUID,
                sourceOrderUUID: orderUUID,
                targetRestaurantsUUIDs: [...selectedAccountUUIDs],
              },
            }}
            secondaryButtonLabel={i18n.t('cancel')}
            secondaryButtonTrackingData={null}
            title={i18n.t('this_action_can_not_be_undone')}
          />
        </BaseActionSheet>
      ) : null}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  blur: {
    bottom: 0,
    left: 0,
    padding: theme.sk.spacing.spaceLarge,
    position: 'absolute',
    right: 0,
  },
  confirmationContentContainer: {
    paddingHorizontal: 0,
  },
  searchContainer: {
    paddingBottom: 8,
    paddingHorizontal: 16,
  },
  searchInput: {
    backgroundColor: theme.colors.background,
  },
  selectAllContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
}));
